import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import AfitdButton from '../../../common/elemental/AfitdButton';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { setClassNameInDefns } from '../../../common/definitionHelper';
import { Grid } from '@material-ui/core';
// import AfitdTypography from '../../../common/elemental/AfitdTypography';
// import AfitdButtonGroup from '../../../common/elemental/AfitdButtonGroup';
import IconButton from '@material-ui/core/IconButton';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { LayoutContext } from '../../../common/contexts';
import { ADMIN_CONSOLE_PAGE_SIZE } from '../../../common/constants';
import { loadActivityStatsApplied } from '../../../common/services/adminServices';
import AfitdPagination from '../../../common/elemental/AfitdPagination';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import AfitdDatePicker from '../../../common/elemental/AfitdDatePicker';
// import DropDown from './DropDown';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _slice from 'lodash/slice';
import { format } from 'date-fns';
import MonthSelector from './MonthSelector';

// import _get from 'lodash/get';
//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        headerTitle: { fontWeight: 500, fontSize: 20 },
        topBarRightItem: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
        passwordResetButton: { height: 36, marginRight: 8 },
        selectedText: { paddingRight: 16 },
        deleteButton: { height: 36 },
        topBarContainer: { height: 57.66, border: '1px solid #F3F3F3', padding: '0px 10px' },

        root: {
            width: '100%',
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 10,
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
        table: {
            minWidth: 750,
        },
        visuallyHidden: {
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            top: 20,
            width: 1,
        },
        tableRowContainer: {
            maxHeight: 600,
            overflowY: 'scroll',
            '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                background: 'none',
            },
            '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F5F5F5',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0,0,0,0.2)',
            },
        },
        topBarLeftItem: { display: 'flex' },
        jobSeekerButton: { textTransform: 'capitalize' },
        employerButton: { textTransform: 'capitalize' },
        blockedUserContainer: { display: 'flex', alignItems: 'center' },
        tableCellStyle: { padding: '6px 16px' },
        hiresButton: { textTransform: 'capitalize' },
        jobsAppliedButton: { textTransform: 'capitalize' },
        interviewDataButton: { textTransform: 'capitalize' },
        keyboardArrowLeftIcon: {
            fontSize: '32px',
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            padding: '3px',
            borderRadius: '50%',
        },
        keyboardArrowRightIcon: {
            fontSize: '32px',
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            padding: '3px',
            borderRadius: '50%',
        },
        keyboardArrowLeftIconButton: {
            padding: 0,
            color: '#111E14',
            marginRight: '8px',
        },
        keyboardArrowRightIconButton: {
            padding: 0,
            color: '#111E14',
            marginLeft: '8px',
        },
        totalHiredText: { color: '#9B9E9C' },
        totalHiredTextContainer: { paddingRight: 10 },
        jobTitleColumn: { color: '#1D8A8B', cursor: 'pointer', fontWeight: 500 },
        userDropDown: {
            maxWidth: '130px',
            color: '#000',
            [theme.breakpoints.down('sm')]: { maxWidth: '125px' },
        },
        userDropDownContainer: {
            paddingLeft: 25,
        },
        userCountryDropDown: { paddingLeft: 16 },
        notificationIconText: {
            background: 'rgba(17, 30, 20, 0.4)',
            borderRadius: 11,
            fontWeight: 500,
            fontSize: 12,
            padding: '1px 4px',
            color: 'white',
        },
        datePickerContainer: {
            display: 'flex',
            alignItems: 'flex-start',
        },
    }));
};

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

const headCells = [
    { id: 'jsName', numeric: true, disablePadding: true, label: 'Name' },
    { id: 'country', numeric: true, disablePadding: true, label: 'Country' },
    // { id: 'name', numeric: false, disablePadding: true, label: 'Company Name' },
    { id: 'jobTitle', numeric: false, disablePadding: false, label: 'Job Title' },
];

const getDefns = params => {
    const {
        classes,
        props,
        // setUserType,
        handleChangeUserTag,
        handleChangeCountry,
        userType,
        setMonth,
        month,

        // date,
        // handleDateChange,
    } = params;
    const { country, setCountry } = props;
    const USER_OPTIONS = [
        { value: 'Employer', label: 'Employer' },
        { value: 'Job Title', label: 'Job Title' },
        { value: 'Job Seeker', label: 'Job Seeker' },
    ];
    const COUNTRY_OPTIONS = [
        { value: 'United States', label: 'United States' },
        { value: 'USA', label: 'USA' },
    ];

    const defns = {
        rootContainer: { item: true, xl: 9, lg: 9, md: 9, sm: 9, xs: 9 },
        passwordResetButton: { color: 'secondary' },
        topBarContainer: { container: true, alignItems: 'center' },
        topBarLeftItem: { item: true, xl: 7, xs: 7 },
        topBarRightItem: { item: true, xl: 5, xs: 5 },
        selectedText: { color: 'secondary' },
        acceptDeclineButtonGroup: { isHidden: false },

        table2props: { country, setCountry, setMonth, month },
        // date: { value: date, onChange: handleDateChange },
        // auto complete
        userDropDown: {
            title: 'Employer',
            options: USER_OPTIONS,
            value: userType,
            onChange: handleChangeUserTag,
        },
        // auto complete
        countryDropDown: {
            title: 'United State',
            options: COUNTRY_OPTIONS,
            value: country,
            onChange: handleChangeCountry,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};
function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{ 'aria-label': 'select all desserts' }}
                    />
                </TableCell>
                {headCells.map(headCell => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.id === 'jobTitle' ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                            style={{
                                fontWeight: 'bold',
                                color: '#111E14',
                                opacity: 0.6,
                            }}
                        >
                            {headCell.label}
                            {/* {orderBy === headCell.id ? <span className={classes.visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</span> : null} */}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar2 = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { userType, setUserType, country, setCountry, setMonth, month } = props;

    //---------------------------------------------------------------------
    // local state
    //---------------------------------------------------------------------

    // const [userType, setUserType] = useState('Employer');
    // const [country, setCountry] = useState('United States');
    // const [data, setData] = useState({ date: new Date() });

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    // const handleDateChange = evt => {
    //     setData({ ...data, date: evt });
    // };
    const handleChangeUserTag = val => {
        setUserType(val);
    };
    const handleChangeCountry = val => {
        setCountry(val);
    };

    const defns = getDefns({
        classes,
        props,

        userType,
        setUserType,
        handleChangeUserTag,
        country,
        handleChangeCountry,
        // data,
        // setData,
        // handleDateChange,
    });
    return (
        <Grid {...defns.topBarContainer}>
            <Grid {...defns.topBarLeftItem}>
                <div {...defns.datePickerContainer}>
                    <IconButton {...defns.keyboardArrowLeftIconButton}>
                        <KeyboardArrowLeftIcon {...defns.keyboardArrowLeftIcon} />
                    </IconButton>
                    {/* <AfitdDatePicker {...defns.date} /> */}
                    <div>
                        <MonthSelector value={month} onChange={newVal => setMonth(newVal)} />
                    </div>
                    <IconButton {...defns.keyboardArrowRightIconButton}>
                        <KeyboardArrowRightIcon {...defns.keyboardArrowRightIcon} />
                    </IconButton>
                </div>
            </Grid>
            <Grid {...defns.topBarRightItem}>
                <AfitdButton {...defns.deleteButton}>
                    <DeleteOutlineIcon />
                    &nbsp;Delete
                </AfitdButton>
            </Grid>
        </Grid>
    );
};

const ActivityStatsTableAppliedJobs = props => {
    // const classes = useStyles();
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('calories');
    const [data, setData] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [queryCursors, setQueryCursors] = React.useState([]);
    const [month, setMonth] = React.useState({ label: format(new Date(), 'MMMM yyyy'), value: format(new Date(), 'yyyy-MM') });
    // const [selected, setSelected] = React.useState([]);
    const classes = useStyles(/*{param1, param2}*/)();
    const layoutContext = useContext(LayoutContext);
    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;
    const { totalCounts /*todayCount*/ } = layoutContext;
    const totalCount = totalCounts?.appliedJobCount || 0;
    const totalPage = Math.ceil(totalCount / ADMIN_CONSOLE_PAGE_SIZE);
    // const classes = useToolbarStyles();
    const defns = getDefns({ classes, props, setMonth, month });
    // props
    const { selected, setSelected /*, userType */ } = props; //userType, country

    React.useEffect(() => {
        const loadData = async () => {
            const { result, lastDoc } = await loadActivityStatsApplied({ setData, setQueryCursors, month: month.value });
            setData(result);
            setQueryCursors(prev => [...prev, lastDoc]); // add cursor
        };
        loadData();
    }, [month]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const handleClickPrev = async () => {
        const { result, lastDoc } = await loadActivityStatsApplied({ queryCursors, mode: 'prev', month: month.value });
        setData(result);
        setQueryCursors(prev => [..._slice(prev, 0, prev.length - 2), lastDoc]); // remove last cursor
    };

    const handleClickNext = async () => {
        const { result, lastDoc } = await loadActivityStatsApplied({ queryCursors, month: month.value }); // default mode is 'next'
        setData(result);
        setQueryCursors(prev => [...prev, lastDoc]); // add cursor
    };
    const handleSelectAllClick = event => {
        if (event.target.checked) {
            const newSelecteds = data.map(n => n.uid);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };
    console.log({ data });
    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }

        setSelected(newSelected);
    };

    const handleChangePage = (evt, value) => {
        if (value > page) {
            handleClickNext();
        } else {
            // go to prev unless already at page 1
            if (page !== 1) handleClickPrev();
        }
        setPage(value);
    };
    const isSelected = name => selected.indexOf(name) !== -1;
    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
                <EnhancedTableToolbar2 {...defns.table2props} />
                <div {...defns.tableRowContainer}>
                    <TableContainer>
                        <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table">
                            <EnhancedTableHead
                                classes={classes}
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={handleSelectAllClick}
                                onRequestSort={handleRequestSort}
                                rowCount={data.length}
                            />
                            <TableBody>
                                {stableSort(data, getComparator(order, orderBy)).map((row, index) => {
                                    const isItemSelected = isSelected(row.uid);
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow
                                            hover
                                            onClick={event => handleClick(event, row.uid)}
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.uid}
                                            selected={isItemSelected}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={isItemSelected} inputProps={{ 'aria-labelledby': labelId }} />
                                            </TableCell>

                                            <TableCell component="th" id={labelId} scope="row" padding="none" style={{ fontWeight: 'bold' }}>
                                                {row.jsName}
                                            </TableCell>
                                            <TableCell component="th" id={labelId} scope="row" padding="none" style={{ fontWeight: 'bold' }}>
                                                {row.jsCountry}
                                            </TableCell>

                                            <TableCell align="right" {...defns.jobTitleColumn}>
                                                {row.jobTitle}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </Paper>
            <div {...defns.paginationContainer}>
                <AfitdPagination count={totalPage} color="secondary" page={page} onChange={handleChangePage} disablePage={true} />
            </div>
        </div>
    );
};

export default ActivityStatsTableAppliedJobs;
